import React, { useImperativeHandle, useRef, useState } from "react";
import { RCCAlert, RCCButton, RCCGrid, RCCHookToast, RCCIcon, RCCInput, RCCRefModalProps, RCCServicesApi, RCCSwitch, RCCTextArea, RCCTooltip } from "react-common-components";
import { useFieldArray, useForm, useWatch } from "react-hook-form";
import Audit from "src/components/Audit";
import DateTimePickerRange from "src/components/DateTimePickerRange";
import DeleteModal from "src/components/DeleteModal";
import Modal from "src/components/Modal";
import ModalSide from "src/components/Modal/styles";
import Tabs from "src/components/Tabs";
import { useTranslation } from "src/context/Translations/context";
import { getValidationErrors, standardErrorMessages } from "src/utils/errors";
import { v4 } from "uuid";
import Permissions from "src/components/Permissions";
import { simplePermissionType } from "src/components/Permissions/interface";
import SortableContainer from "src/components/Sortable/Container";
import SortableHandle from "src/components/Sortable/Handle";
import SortableItem from "src/components/Sortable/Item";
import Title from "src/components/Title";
import { useAuth } from "src/context/Auth/context";
import LovBcgItems from "src/components/Lov/BcgItems";
import { restrictToVerticalAxis } from "src/components/Sortable/Container/modifiers";
import { IMatbc001, IMatbc002, IMatbc003, IModalMatrizBcg, IRefMatrizBcgModal } from "./interface";
import { schemaDeleteMatrizBcg, schemaMatrizBcg } from "./schemas";


const ModalMatrizBcg: React.ForwardRefRenderFunction<IRefMatrizBcgModal, IModalMatrizBcg> = ({ 
	onCallbackAdd,
	onCallbackChange,
	onCallbackDelete
}, ref) => {

	const { translate } = useTranslation();
	const { user } = useAuth();

	const [selectedMatrizBcg, setSelectedMatrizBcg] = useState<IMatbc001>({} as IMatbc001);
	const [isAdd, setIsAdd] = useState(false);

	const refModalAddOrChange = useRef<RCCRefModalProps>(null);
	const modalDelete = useRef<RCCRefModalProps>(null);

	const { addToast } = RCCHookToast();

	const { 
		handleSubmit, 
		register, 
		reset,
		setValue,
		control,
		unregister,
		setError, 
		formState: {errors},
		clearErrors } = useForm();

	const { 
		handleSubmit: handleSubmitDelete, 
		register: registerDelete, 
		reset : resetDelete, 
		setError : setErrorDelete, 
		formState: { errors : errorsDelete }, 
		clearErrors: clearErrorsDelete 
	} = useForm();

	const { 
		fields, 
		remove,
		append,
		move
	} = useFieldArray({
		control,
		name: "matbc003",
		shouldUnregister: true
	});
	

	const actionButtonAdd = () => {
		remove();
		reset();
		setIsAdd(true);
		setSelectedMatrizBcg({} as IMatbc001);

		const initialPermission =
		[{
			mac002Id:0,
			mac002Efa005Id: user.efa005Id,
			mac002Efa005Nome: user.efa005Nome,
			mac002Permissao: parseInt(simplePermissionType.VIEW_AND_EDIT, 10) 
		}] as IMatbc002[];

		setValue("matbc002", initialPermission);

		refModalAddOrChange.current?.openModal();
	};

	const actionButtonChange = (id: number) => {
		reset();
		setIsAdd(false);
		queryMatrizBcg(id);
	};

	const actionButtonDelete = (mac001Id: number, mac001Titulo: string) => {
		resetDelete();
		setSelectedMatrizBcg({
			mac001Id,
			mac001Titulo
		} as IMatbc001);
		modalDelete.current?.openModal();
	};

	useImperativeHandle(ref, () => ({
		actionButtonAdd,
		actionButtonChange,
		actionButtonDelete
	}));

	const queryMatrizBcg = async (matrizBcgId: number) => {
		try{
			const { data } = await RCCServicesApi.getRequest({
				method: "AnalysisMatrizBcg/Select",
				urlParameters: {
					id: matrizBcgId
				}
			});

			setSelectedMatrizBcg(data);
			append(data.matbc003);
			refModalAddOrChange.current?.openModal();
		}catch(err){
			standardErrorMessages(err, translate("Erro ao carregar Matriz BCG"), addToast);
		}
	};

	const onSubmit = async (data: any) => {
		try{
			clearErrors();
			refModalAddOrChange.current?.setLoadingButton();

			const objToString = (obj: any) => {
				if(typeof obj === "object")
					return obj?.value;
				if(typeof obj === "number")
					return obj.toString();
				if(typeof obj !== "string")
					return "";
				return obj;
			};
		

			const dataChanged = data;
			dataChanged.mac001Ativo = data.mac001Ativo ? "S" : "N";
		
			dataChanged.matbc002 = data?.matbc002 ? data.matbc002.map((item: IMatbc002) => ({
				mac002Efa004Id: Number(objToString(item.mac002Efa004Id)) > 0 ? Number(objToString(item.mac002Efa004Id)) : null,
				mac002Efa005Id: Number(objToString(item.mac002Efa005Id)) > 0 ? Number(objToString(item.mac002Efa005Id)) : null,
				mac002Permissao: Number(objToString(item.mac002Permissao))
			})).filter((e: any) => e.mac002Efa004Id || e.mac002Efa005Id) : [];

			dataChanged.matbc003 = data?.matbc003 ? data.matbc003.filter((dt:any) => dt.mac003Taa013Id !== null).map((item: IMatbc003) => ({
				...item,
				mac003Taa013Id: Number(item.mac003Taa013Id)
			})) : [];

			await schemaMatrizBcg(translate).validate({...dataChanged},{
				abortEarly: false
			});

			// verifica se tem permissão de Visializar e Editar
			if(!dataChanged.matbc002.filter((x: any) => x.mac002Permissao.toString() === simplePermissionType.VIEW_AND_EDIT).length){
				refModalAddOrChange.current?.resetLoadingButton();
				addToast({
					color: "yellow",
					description: translate("Ao menos uma permissão deve ser (Visualizar e Editar)"),
					title: translate("Atenção")
				});
				return;
			}

			if(isAdd){
				await RCCServicesApi.postRequest({
					method: "AnalysisMatrizBcg/Insert",
					body: {
						...dataChanged
					}
				});

				if(onCallbackAdd)
					onCallbackAdd();

			}else{
				await RCCServicesApi.postRequest({
					method: "AnalysisMatrizBcg/Update",
					body: {
						...dataChanged,
						mac001Id: selectedMatrizBcg.mac001Id
					}
				});

				if(onCallbackChange)
					onCallbackChange();
			}

			addToast({
				color: "green",
				description: isAdd ? translate("Análise adicionada com sucesso") : translate("Análise alterada com sucesso"),
				title: translate("Sucesso")
			});

			refModalAddOrChange.current?.resetLoadingButton();
			refModalAddOrChange.current?.closeModal();

		}catch(err){
			refModalAddOrChange.current?.resetLoadingButton();
			if(getValidationErrors(err, setError)){
				addToast({
					title: translate("Erro"),
					description: translate("Verifique os erros no formulário e tente novamente"),
					color: "yellow"
				});
				return;
			}

			standardErrorMessages(err, isAdd ? translate("Erro ao adicionar análise") : translate("Erro ao alterar análise"), addToast);
		}
	};

	const submitDelete = async (data: any) => {
		try{
			clearErrorsDelete();	
			modalDelete.current?.setLoadingButton();

			await schemaDeleteMatrizBcg(translate).validate({...data, "mac001TituloConfirmation": selectedMatrizBcg.mac001Titulo},{
				abortEarly: false
			});
			
			await RCCServicesApi.deleteRequest({
				method: "AnalysisMatrizBcg/Delete",
				urlParameters: {
					id: selectedMatrizBcg.mac001Id
				}
			});

			addToast({
				color: "green",
				description: translate("Análise excluída com sucesso"),
				title: translate("Sucesso")
			});
			modalDelete.current?.resetLoadingButton();
			modalDelete.current?.closeModal();

			if(onCallbackDelete)
				onCallbackDelete(selectedMatrizBcg.mac001Id);

		}catch(err){
			modalDelete.current?.resetLoadingButton();

			if(getValidationErrors(err, setErrorDelete))
				return;

			standardErrorMessages(err, translate("Erro ao excluir análise"), addToast);
		}
		
	};

	const defaultValueLov = (id: number, description: string) => {
		if(id && description)
			return {
				value: id,
				label: description
			};
		if(id) return id;
		return null;
	};

	const handleDragEnd = (activeId: string, overId: string) => {
		const activeIndex = fields.findIndex((checks) => checks.id === activeId);
		const overIndex = fields.findIndex((checks) => checks.id === overId);
		move(activeIndex, overIndex);	
	};

	const permissionsWatch = useWatch({
		control,
		name: "matbc002"
	});

	return (
		<>
			<DeleteModal
				ref={modalDelete}
				title= {translate("Exclusão de análise")}
				description= {`${translate("Deseja realmente excluir a análise")}: ${selectedMatrizBcg.mac001Titulo}?`}
				onSubmit = {handleSubmitDelete(submitDelete)} 
				height="250px"
			>
				<RCCGrid
					container
					item
					xs={10}
				>
					<RCCInput 
						register={registerDelete}
						name="mac001Titulo"
						label={translate("Digite o título da análise para excluí-la")}
						maxLength={200}
						error={errorsDelete.mac001Titulo}
						required
					/>
				</RCCGrid>
			</DeleteModal>
			
			<Modal
				ref={refModalAddOrChange}
				onSubmit={handleSubmit(onSubmit)}
				header={{
					title: isAdd ? translate("Inserir Análise"): translate("Alterar Análise"),
					subtitle: !isAdd ? `${translate("Análise")}: ${selectedMatrizBcg.mac001Titulo}`: "",
					icon: {
						name: isAdd? "plus": "pencil"
					}
				}}
				styleInline={ModalSide}
			>

				<Tabs 
					name="tabsMatrizBcg"
					tabInit={0}
					options={[{
						label: translate("Dados Gerais"),
						content: (
							<>
								<RCCGrid 
									container 
									item 
									direction="column"
									alignItems="center"
								>
									<RCCGrid 
										container
										item
										xs={12}
										sm={10}
										alignItems="center"
										justify="flex-end"
									>
										<Audit data={selectedMatrizBcg} type="pop"/>
									</RCCGrid>
									<RCCGrid 
										container 
										item
										xs={12}
										sm={10}
									>
										<RCCInput 
											name="mac001Titulo"
											register={register}
											label={translate("Título")}
											defaultValue={selectedMatrizBcg.mac001Titulo ?? ""}
											error={errors.mac001Titulo}
											maxLength={600}
											required
										/>
									</RCCGrid>
									<RCCGrid 
										container 
										item
										xs={12}
										sm={10}
									>	
										<RCCTextArea 
											register={register}
											label={translate("Descrição")}
											name="mac001Descricao"
											error={errors.mac001Descricao}
											defaultValue={selectedMatrizBcg.mac001Descricao}
											maxLength={4000}
										/>	
									</RCCGrid>
									<RCCGrid 
										container 
										item
										xs={12}
										sm={10}
									>	
										<DateTimePickerRange
											required
											control={control}
											label={translate("Período")}
											labelSeparator={translate("até")}
											nameInit="mac001Datainicial"
											nameEnd="mac001Datafinal"
											defaultValueInit={selectedMatrizBcg.mac001Datainicial || new Date}
											defaultValueEnd={selectedMatrizBcg.mac001Datafinal || new Date}
											error={errors.mac001Datainicial || errors.mac001Datafinal}
											type="datePicker"
										/>
									</RCCGrid>
									<RCCGrid 
										container 
										item
										xs={12}
										sm={10}
										style={{marginTop: 15}}
									>		
										<RCCSwitch
											name="mac001Ativo"
											label={translate("Ativo")}
											register={register}
											defaultChecked={selectedMatrizBcg.mac001Ativo === "S" || isAdd}
										/>
									</RCCGrid>
								</RCCGrid>
							</>
						)}, {
						label: translate("Itens BCG"),
						content: (
							<RCCGrid
								container
								item
								justifyContent="center"
							>
								<RCCGrid
									container
									item
									direction="column"
									alignItems="center"
									justifyContent="center"
									xs={12} sm={10}
								>
									<RCCGrid
										container
										item
										justifyContent="flex-end"
									>		
										<RCCTooltip
											color="purple"
											text={translate("Adicionar")}
										>
											<RCCButton
												color="purple"
												icon={{
													name: "plus"
												}}
												layout="isRounded"
												size="md"
												onClick={() => {
													clearErrors("matbc003");
													append({
														id: v4()
													}as IMatbc003);
												}}
											/>
										</RCCTooltip>
									</RCCGrid>
								
									<RCCGrid
										container
										item
									>
										{errors?.matbc003 && (
											<>
												<RCCIcon name="infoCircle" size={18} color="var(--red)"/>
												<Title color="red" size="xs">{errors?.matbc003.message}</Title>
											</>
										)}

										{fields.length === 0 && (
											<RCCAlert
												color="grey"
												title={translate("Nenhum item BCG adicionado")}
												description={translate("Aperte no botão acima para adicionar")}
											/>
										)}

										<SortableContainer 
											items={fields}
											onDragEnd={handleDragEnd}
											modifiers={[restrictToVerticalAxis]}
										>	
											{fields.map((itemBcg: any, index) => (					
												<SortableItem id={itemBcg.id} key={itemBcg.id}>
													<RCCGrid
														container
														item
														alignItems="center"
													>
														<input 
															type="text"
															defaultValue={itemBcg.id}
															{...register(`matbc003[${index}].id`)} 
															style={{ display: "none" }} 
															readOnly
														/>
														<RCCGrid
															container
															item
															justifyContent="center"
															xs={12}
															sm={10}
														>
															<LovBcgItems 
																name={`matbc003[${index}].mac003Taa013Id`}
																label=""
																control={control}
																defaultValue={defaultValueLov(itemBcg.mac003Taa013Id, itemBcg.mac003Taa013Descricao)}	
																styleInline="height: 34px;"
																isCreatable
																onChangeCapture={() => {
																	unregister();
																	clearErrors("matbc003");
																}}
															/>
														</RCCGrid>
														<RCCGrid
															container
															item
															justifyContent="flex-end"
															xs={12}
															sm={2}
														>
															<SortableHandle>
																<RCCTooltip
																	text={translate("Mover")}
																	color="grey"
																>
																	<RCCButton
																		color="transparent"
																		icon={{
																			name: "arrows",
																			color: "var(--grey)"
																		}}
																		layout="isRounded"
																		tabIndex={-1}
																	/>
																</RCCTooltip>
															</SortableHandle>
															<RCCTooltip
																text={translate("Excluir")}
																color="red"
															>
																<RCCButton
																	icon={{name: "trash"}}
																	color="red"
																	size="sm"
																	layout="isRounded"
																	onClick={() => {
																		clearErrors("matbc003");
																		remove(index);
																	}}
																/>
															</RCCTooltip>
														</RCCGrid>
														<hr />
													</RCCGrid>	
												</SortableItem>
											))}
										</SortableContainer>
									
									</RCCGrid>
								</RCCGrid>
							</RCCGrid>
						)},{
						label: translate("Permissões"),
						content: (
							<>
								<RCCGrid
									container
									item
									justifyContent="center"
								>
									<RCCGrid
										container
										item
										direction="column"
										alignItems="center"
										justifyContent="center"
										xs={12} sm={10}
									>
										<Permissions
											register={register}
											unregister={unregister}
											control={control}
											error={errors}
											clearErrors={clearErrors}
											permissionsNames= {{
												nameObject: "matbc002",
												nameOfID: "mac002Id",
												nameOfSelectUser: "mac002Efa005Id",
												nameOfSelectGroup: "mac002Efa004Id",
												nameOfSelectPermission: "mac002Permissao",
												nameOfSelectGroupDescription:"mac002Efa004Nome",
												nameOfSelectUserDescription:"mac002Efa005Nome",
												defaultValue: isAdd ? permissionsWatch: selectedMatrizBcg.matbc002
											}}
											permissionType="simple"
										/>
									</RCCGrid>
								</RCCGrid>	
							</>
						)
					}
					]}
				/>
		
			</Modal>
		</>
	);
};


export default React.forwardRef(ModalMatrizBcg);
